import React, { type ReactNode, useCallback, useRef, memo } from 'react';
import cc from 'classcat';
import useHorizontalScrollEdge from 'hooks/useHorizontalScrollEdge';
import useScrollVisibility from 'hooks/useScrollVisibility';
import useScrollEventOnce from 'hooks/useScrollEventOnce';
import withLazyStyle from 'components/LazyStyle';
import ArrowButton from './components/ArrowButton';
import style from './rowList.css?lazy';

interface RowListProps {
  className?: string;
  children?: ReactNode;
  scrollEventOnce?: () => void;
}

const RowList = (props: RowListProps) => {
  const { children, className, scrollEventOnce } = props;
  const contentRef = useRef<HTMLDivElement>(null);
  const { rightEdgeReached, leftEdgeReached, handleScroll } = useHorizontalScrollEdge();
  const { hasHorizontalScroll } = useScrollVisibility(contentRef);

  useScrollEventOnce(contentRef, scrollEventOnce);

  const handleClickPrev = useCallback(() => {
    if (!contentRef.current) return;
    contentRef.current.scrollLeft -= contentRef.current.clientWidth;
  }, []);

  const handleClickNext = useCallback(() => {
    if (!contentRef.current) return;
    contentRef.current.scrollLeft += contentRef.current.clientWidth;
  }, []);

  const rootClassName = cc(['rowList', className, hasHorizontalScroll && 'rowList--hasScroll']);

  if (!children) return null;

  return (
    <div className={rootClassName}>
      {hasHorizontalScroll && !leftEdgeReached && (
        <ArrowButton
          direction="left"
          className="rowList__arrow rowList__arrow--left"
          handleClick={handleClickPrev}
        />
      )}
      {hasHorizontalScroll && !rightEdgeReached && (
        <ArrowButton
          direction="right"
          className="rowList__arrow rowList__arrow--right"
          handleClick={handleClickNext}
        />
      )}
      <div className="rowList__content hide-scrollbar" ref={contentRef} onScroll={handleScroll}>
        {children}
      </div>
    </div>
  );
};

export default memo(withLazyStyle(style)(RowList));
